import { createRunAfterSignal } from './runAfterSignal';
import type { SwSignal } from './swSignal';

export interface BoundedRunAfterSignal {
  /**
   * Asks for one run at the next emit of the signal. Asking while it waits
   * changes nothing; a new wait is refused (`false`) once `maxWaits` ended
   * without `resetWaits`, so the caller gives up instead of waiting for ever.
   * The refusal starts the count over: a later ask (the user tries again)
   * gets its own waits.
   */
  ask(): boolean;
  /** The wait was fulfilled: the next one starts counting again. */
  resetWaits(): void;
  /** Drops the wait and its count: what was asked for is no longer wanted. */
  cancel(): void;
  isWaiting(): boolean;
}

/**
 * `createRunAfterSignal` for a fact that may never come: it waits at most
 * `maxWaits` times in a row (enough for a slow start, not a loop for the
 * case that never ends).
 */
export function createBoundedRunAfterSignal(
  signal: SwSignal,
  run: () => void,
  maxWaits: number
): BoundedRunAfterSignal {
  const afterSignal = createRunAfterSignal(signal, run);
  let waits = 0;

  const resetWaits = (): void => {
    waits = 0;
  };

  return {
    ask: () => {
      if (afterSignal.isWaiting()) {
        return true;
      }
      if (waits >= maxWaits) {
        resetWaits();
        return false;
      }
      waits += 1;
      afterSignal.ask();
      return true;
    },
    resetWaits,
    cancel: () => {
      afterSignal.cancel();
      resetWaits();
    },
    isWaiting: afterSignal.isWaiting,
  };
}
